import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { cn } from '@goaltree/ui/lib/utils'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import { ChevronDown, PencilLine } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { NotesThread } from '@/components/coaching/coach-notes'
import { UserAvatar } from '@/components/user-avatar'

type Thread = FunctionReturnType<typeof api.coaching.goalThreads>[number]

type Tree = {
  _id: Id<'nodes'>
  isOwner: boolean
  isInbox?: boolean
  viewerIsCoach?: boolean
  ownerUserId: string
  ownerName?: string
  milestones: {
    lastEditedBy?: string
    targets: { tasks: { lastEditedBy?: string }[] }[]
  }[]
}

const hasMarkers = (tree: Tree) =>
  tree.milestones.some(
    (m) => m.lastEditedBy || m.targets.some((tg) => tg.tasks.some((tk) => tk.lastEditedBy)),
  )

// The owner has now seen their coach's edits: markers stay up while they're on
// the goal and are gone the next time (on leave, not on open).
export function useClearCoachMarkers(tree: Tree | null | undefined) {
  const clear = useMutation(api.coaching.clearEditMarkers)
  const marked = useRef(new Set<string>())
  if (tree?.isOwner && hasMarkers(tree)) marked.current.add(tree._id)
  const goalId = tree?._id
  useEffect(
    () => () => {
      if (goalId && marked.current.delete(goalId)) void clear({ goalId }).catch(() => {})
    },
    [goalId, clear],
  )
}

export function GoalCoachStrip({ tree, className }: { tree: Tree; className?: string }) {
  if (tree.viewerIsCoach) return <CoachBanner tree={tree} className={className} />
  if (tree.isOwner && !tree.isInbox) return <ClientGoalNotes tree={tree} className={className} />
  return null
}

function CoachBanner({ tree, className }: { tree: Tree; className?: string }) {
  const { t } = useTranslation()
  return (
    <div
      className={cn(
        'flex items-start gap-2.5 rounded-xl border border-primary/30 bg-primary/5 px-3.5 py-2.5 text-sm',
        className,
      )}
    >
      <PencilLine className="mt-0.5 size-4 shrink-0 text-primary-text" />
      <p className="min-w-0 flex-1 text-muted-foreground">
        <span className="font-semibold text-foreground">
          {t('coaching.goal.coachingBanner', { name: tree.ownerName ?? '' })}
        </span>{' '}
        {t('coaching.goal.coachingHint')}
      </p>
      <Link
        to="/coaching/$clientId"
        params={{ clientId: tree.ownerUserId }}
        className="shrink-0 text-xs font-semibold text-primary-text hover:underline"
      >
        {t('coaching.goal.openClient')}
      </Link>
    </div>
  )
}

function ClientGoalNotes({ tree, className }: { tree: Tree; className?: string }) {
  const threads = useQuery(api.coaching.goalThreads, { goalId: tree._id })
  if (!threads?.length) return null
  return (
    <div className={cn('space-y-2', className)}>
      {threads.map((th) => (
        <ClientThread key={th.linkId} goalId={tree._id} thread={th} />
      ))}
    </div>
  )
}

function ClientThread({ goalId, thread }: { goalId: Id<'nodes'>; thread: Thread }) {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const { coach, endedAt } = thread
  return (
    <div
      className={cn('rounded-xl border', thread.active ? 'border-coach/30 bg-coach/5' : 'bg-card')}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full cursor-pointer items-center gap-2.5 px-3.5 py-2.5 text-left"
      >
        <UserAvatar
          image={coach.image}
          name={coach.name}
          size={24}
          className={cn(
            'size-6 shrink-0 rounded-full',
            thread.active ? 'ring-2 ring-coach/60' : 'opacity-70',
          )}
        />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold">
          {t('coaching.goal.notesWith', { name: coach.name })}
        </span>
        {!thread.active && endedAt !== null && (
          <span className="shrink-0 text-xs text-muted-foreground">
            {t('coaching.goal.ended', {
              date: new Date(endedAt).toLocaleDateString(i18n.language, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              }),
            })}
          </span>
        )}
        <ChevronDown
          className={cn('size-4 text-muted-foreground transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <NotesThread
          linkId={thread.linkId}
          goalId={goalId}
          viewerIsCoach={false}
          coachName={coach.name}
          clientName=""
          ended={!thread.active}
          className={cn('border-t px-3.5 py-3', thread.active && 'border-coach/20')}
        />
      )}
    </div>
  )
}
